import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, Circle, ListChecks } from "lucide-react";
import { IEP_SECTIONS } from "@/lib/iepSections";

export default function MeetingDialog({ meeting, onClose }) {
  const [active, setActive] = useState("overview");
  const [sections, setSections] = useState(meeting.sections || {});
  const [notes, setNotes] = useState(meeting.notes || "");
  const [actionItems, setActionItems] = useState(meeting.action_items || []);
  const [actionInput, setActionInput] = useState("");
  const [agenda, setAgenda] = useState(meeting.ai_agenda || null);
  const [savingTop, setSavingTop] = useState(false);
  const [generatingAgenda, setGeneratingAgenda] = useState(false);

  const completedCount = useMemo(
    () => IEP_SECTIONS.filter((s) => sections[s.id]?.completed).length,
    [sections]
  );

  const generateAgenda = async () => {
    setGeneratingAgenda(true);
    try {
      const r = await api.post("/meetings/ai-agenda", {
        meeting_id: meeting.id,
        meeting_type: meeting.title,
      });
      if (r.data.parsed) {
        setAgenda(r.data.parsed);
        toast.success("Agenda ready");
      } else {
        toast.error("Could not parse AI agenda");
      }
    } finally {
      setGeneratingAgenda(false);
    }
  };

  const saveTop = async () => {
    setSavingTop(true);
    try {
      await api.put(`/meetings/${meeting.id}/notes`, { notes, action_items: actionItems });
      toast.success("Saved");
    } finally {
      setSavingTop(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-6xl p-0 h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-[#E8E9E6]">
          <DialogTitle className="font-heading text-2xl ink">{meeting.title}</DialogTitle>
          <div className="text-xs ink-muted mt-1">
            {new Date(meeting.scheduled_at).toLocaleString()} · {meeting.duration_minutes} min · {meeting.location || "Location TBD"}
          </div>
          <div className="mt-3 flex items-center gap-3">
            <div className="text-xs ink-muted">Section progress</div>
            <div className="flex-1 h-2 bg-[#F0EBF6] rounded-full overflow-hidden max-w-xs">
              <div className="h-full bg-[#6E5AA0] transition-all" style={{ width: `${(completedCount / IEP_SECTIONS.length) * 100}%` }} />
            </div>
            <div className="text-xs ink-muted font-medium">{completedCount} / {IEP_SECTIONS.length}</div>
          </div>
        </DialogHeader>

        <div className="flex-1 flex min-h-0">
          {/* Left sidebar */}
          <aside className="w-64 border-r border-[#E8E9E6] bg-[#FBFBF9] overflow-y-auto py-3">
            <button
              onClick={() => setActive("overview")}
              data-testid="meeting-section-overview"
              className={`w-full text-left px-4 py-2.5 flex items-center gap-3 text-sm transition-all ${active === "overview" ? "bg-white border-l-2 border-[#6E5AA0] ink font-medium" : "ink-muted hover:bg-white"}`}
            >
              <ListChecks size={15} />
              <span>Overview & Notes</span>
            </button>
            <div className="overline px-4 pt-4 pb-2">IEP / ARD Sections</div>
            {IEP_SECTIONS.map((s) => {
              const done = sections[s.id]?.completed;
              const isActive = active === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setActive(s.id)}
                  data-testid={`meeting-section-${s.id}`}
                  className={`w-full text-left px-4 py-2 flex items-center gap-3 text-sm transition-all ${isActive ? "bg-white border-l-2 border-[#6E5AA0] ink font-medium" : "ink-muted hover:bg-white"}`}
                >
                  {done ? <CheckCircle2 size={14} className="text-[#769B7B] shrink-0" /> : <Circle size={14} className="text-[#C9C9C5] shrink-0" />}
                  <span className="truncate">{s.title}</span>
                </button>
              );
            })}
          </aside>

          {/* Right pane */}
          <div className="flex-1 overflow-y-auto p-6 sm:p-8">
            {active === "overview" ? (
              <OverviewPane
                meeting={meeting}
                notes={notes}
                setNotes={setNotes}
                actionItems={actionItems}
                setActionItems={setActionItems}
                actionInput={actionInput}
                setActionInput={setActionInput}
                agenda={agenda}
                generatingAgenda={generatingAgenda}
                onGenerate={generateAgenda}
              />
            ) : (
              <SectionPane
                key={active}
                meetingId={meeting.id}
                section={IEP_SECTIONS.find((s) => s.id === active)}
                value={sections[active]}
                onSaved={(v) => setSections({ ...sections, [active]: v })}
              />
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-[#E8E9E6] bg-white">
          <Button variant="ghost" onClick={onClose} data-testid="meeting-close">Close</Button>
          {active === "overview" && (
            <Button
              onClick={saveTop}
              disabled={savingTop}
              className="bg-[#6E5AA0] hover:bg-[#574785] text-white"
              data-testid="meeting-save-notes"
            >
              {savingTop ? "Saving…" : "Save notes"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OverviewPane({ notes, setNotes, actionItems, setActionItems, actionInput, setActionInput, agenda, generatingAgenda, onGenerate }) {
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-heading text-lg font-medium ink">AI agenda</h4>
          <Button size="sm" variant="outline" onClick={onGenerate} disabled={generatingAgenda} data-testid="generate-agenda-button">
            <Sparkles size={14} className="mr-2" /> {generatingAgenda ? "Generating…" : agenda ? "Regenerate" : "Generate"}
          </Button>
        </div>
        {!agenda && !generatingAgenda && (
          <Card className="p-6 border-dashed border-[#D8D9D5] bg-[#FBFBF9] text-sm ink-muted text-center">
            Generate a high-level agenda. For each ARD section, open the section in the sidebar to get specific talking points.
          </Card>
        )}
        {agenda && (
          <div className="space-y-3 text-sm">
            {agenda.agenda?.map((sec, i) => (
              <div key={i} className="border border-[#EDEDE9] rounded-xl p-4 bg-white">
                <div className="flex items-center justify-between">
                  <div className="font-medium ink">{sec.section}</div>
                  <div className="text-xs ink-muted">{sec.minutes} min</div>
                </div>
                <ul className="mt-2 space-y-1 list-disc list-inside ink-muted">
                  {sec.talking_points?.map((p, j) => <li key={j}>{p}</li>)}
                </ul>
              </div>
            ))}
            {agenda.key_reminders?.length > 0 && (
              <div className="border border-[#EDEDE9] rounded-xl p-4 bg-[#EFEAF5]">
                <div className="font-medium ink mb-2">Reminders</div>
                <ul className="space-y-1 list-disc list-inside ink-muted">{agenda.key_reminders.map((q, i) => <li key={i}>{q}</li>)}</ul>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="space-y-4">
        <h4 className="font-heading text-lg font-medium ink">General notes</h4>
        <Textarea rows={8} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="High-level notes from the meeting…" data-testid="meeting-notes-area" />
        <div>
          <div className="font-medium ink mb-2">Action items</div>
          <div className="flex gap-2">
            <Input value={actionInput} onChange={(e) => setActionInput(e.target.value)} placeholder="Add an action item" data-testid="action-item-input" />
            <Button
              variant="outline"
              onClick={() => {
                if (actionInput.trim()) {
                  setActionItems([...actionItems, actionInput.trim()]);
                  setActionInput("");
                }
              }}
              data-testid="action-item-add"
            >
              Add
            </Button>
          </div>
          <ul className="mt-3 space-y-2">
            {actionItems.map((a, i) => (
              <li key={i} className="flex items-center justify-between border border-[#EDEDE9] rounded-lg px-3 py-2 text-sm bg-white">
                <span>{a}</span>
                <button onClick={() => setActionItems(actionItems.filter((_, j) => j !== i))} className="text-[#BA6356] text-xs">remove</button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function SectionPane({ meetingId, section, value, onSaved }) {
  const [notes, setNotes] = useState(value?.notes || "");
  const [completed, setCompleted] = useState(!!value?.completed);
  const [ai, setAi] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setNotes(value?.notes || "");
    setCompleted(!!value?.completed);
    setAi(null);
  }, [section.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/meetings/${meetingId}/section`, {
        section_id: section.id,
        notes,
        completed,
      });
      onSaved({ notes, completed, updated_at: new Date().toISOString() });
      toast.success(`${section.short} saved`);
    } catch (e) {
      toast.error("Could not save section");
    } finally {
      setSaving(false);
    }
  };

  const askAi = async () => {
    setLoadingAi(true);
    try {
      const r = await api.post("/meetings/ai-section", {
        meeting_id: meetingId,
        section_id: section.id,
        section_title: section.title,
        section_description: section.points.join("; "),
      });
      if (r.data.parsed) setAi(r.data.parsed);
      else toast.error("AI couldn't return structured guidance");
    } finally {
      setLoadingAi(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid={`section-pane-${section.id}`}>
      <div>
        <div className="overline">ARD / IEP section</div>
        <h3 className="font-heading text-2xl font-medium ink mt-2">{section.title}</h3>
      </div>

      <Card className="p-5 border border-[#E8E9E6] bg-white">
        <div className="font-medium ink mb-3 text-sm">What this section covers</div>
        <ul className="space-y-1.5 text-sm ink-muted list-disc list-inside">
          {section.points.map((p, i) => <li key={i}>{p}</li>)}
        </ul>
      </Card>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-heading text-lg font-medium ink">AI guidance</h4>
          <Button size="sm" variant="outline" onClick={askAi} disabled={loadingAi} data-testid={`section-ai-${section.id}`}>
            <Sparkles size={14} className="mr-2" /> {loadingAi ? <span className="calm-pulse">Thinking…</span> : ai ? "Refresh" : "Get talking points"}
          </Button>
        </div>
        {ai ? (
          <div className="space-y-3 text-sm">
            {ai.summary && (
              <Card className="p-4 bg-[#EFEAF5] border border-[#E8E9E6]">
                <p className="ink">{ai.summary}</p>
              </Card>
            )}
            {ai.talking_points?.length > 0 && (
              <Card className="p-4 border border-[#EDEDE9] bg-white">
                <div className="font-medium ink mb-2">Talking points</div>
                <ul className="space-y-1 list-disc list-inside ink-muted">{ai.talking_points.map((p, i) => <li key={i}>{p}</li>)}</ul>
              </Card>
            )}
            {ai.questions_for_team?.length > 0 && (
              <Card className="p-4 border border-[#EDEDE9] bg-white">
                <div className="font-medium ink mb-2">Questions for the team</div>
                <ul className="space-y-1 list-disc list-inside ink-muted">{ai.questions_for_team.map((p, i) => <li key={i}>{p}</li>)}</ul>
              </Card>
            )}
            {ai.documentation_prompts?.length > 0 && (
              <Card className="p-4 border border-[#EDEDE9] bg-white">
                <div className="font-medium ink mb-2">Document in your notes</div>
                <ul className="space-y-1 list-disc list-inside ink-muted">{ai.documentation_prompts.map((p, i) => <li key={i}>{p}</li>)}</ul>
              </Card>
            )}
          </div>
        ) : (
          <Card className="p-5 border-dashed border-[#D8D9D5] bg-[#FBFBF9] text-sm ink-muted text-center">
            Click "Get talking points" for AI suggestions tailored to this student and section.
          </Card>
        )}
      </div>

      <div className="space-y-2">
        <h4 className="font-heading text-lg font-medium ink">Section notes</h4>
        <Textarea
          rows={8}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={`Capture what was discussed and decided for "${section.short}"…`}
          data-testid={`section-notes-${section.id}`}
        />
      </div>

      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm ink cursor-pointer">
          <Checkbox checked={completed} onCheckedChange={(v) => setCompleted(!!v)} data-testid={`section-complete-${section.id}`} />
          Mark this section complete
        </label>
        <Button onClick={save} disabled={saving} className="bg-[#6E5AA0] hover:bg-[#574785] text-white" data-testid={`section-save-${section.id}`}>
          {saving ? "Saving…" : "Save section"}
        </Button>
      </div>
    </div>
  );
}
